export type AppIconInput = {
  displayName?: string | null;
  appIdentifier?: string | null;
  category?: string | null;
};

import { resolveAppleSystemApp } from "./appleSystemApps";
import { IOS_IN_CALL_ICON_SLUG, isIosInCallService } from "./iosInCallApp";
import { IOS_SCREENSHOT_ICON_SLUG, isIosScreenshotBundleId } from "./iosScreenshotApp";
import { resolveLocalSvgIcon } from "./resolveLocalSvgIcon";

/**
 * Slugs verified to exist under public/svgicons/. Heuristic resolution only returns
 * slugs in this set; explicit catalog entries may reference any slug we ship.
 */
export const KNOWN_GOOD_SLUGS = new Set([
  "airbnb",
  "android",
  "discord",
  "facebook",
  "firefox",
  "google",
  "googlechrome",
  "googlemaps",
  "gmail",
  "instagram",
  "ios",
  "messenger",
  "netflix",
  "pinterest",
  "reddit",
  "safari",
  "signal",
  "smartthings",
  "snapchat",
  "spotify",
  "telegram",
  "tiktok",
  "uber",
  "whatsapp",
  "x",
  "youtube",
  "zoom",
  "mailbox",
  "openai",
  "protonmail",
  "bookingdotcom",
  "settings",
  "screenshotui",
  "incall",
  "applemail",
]);

/**
 * Explicit bundle-ID → slug map. Value `null` means no SVG in repo — keep colored dot.
 * Keys are lowercased reverse-domain identifiers.
 */
const IDENTIFIER_ICON_SLUGS: Record<string, string | null> = {
  // Teams: no microsoftteams / teams slug in public/svgicons/
  "com.microsoft.skype.teams": null,
  // Apple Mail: dedicated envelope icon (mailbox.svg is the Mailbox.org brand).
  "com.apple.mobilemail": "applemail",
  "com.google.maps": "googlemaps",
  "com.apple.mobilesafari": "safari",
  "com.spotify.client": "spotify",
  "com.samsung.oneconnect4ios": "smartthings",
  "com.google.ios.youtube": "youtube",
  "com.instagram.android": "instagram",
  "com.instagram.ios": "instagram",
  "net.whatsapp.whatsapp": "whatsapp",
  "com.facebook.facebook": "facebook",
  "com.zhiliaoapp.musically": "tiktok",
  "com.google.chrome.ios": "googlechrome",
  "com.google.android.apps.chrome": "googlechrome",
  "com.discord": "discord",
  "com.hammerandchisel.discord": "discord",
  "org.telegram.messenger": "telegram",
  "ph.telegra.telegraph": "telegram",
  "com.netflix.mediaclient": "netflix",
  "com.google.android.youtube": "youtube",
  "com.reddit.frontpage": "reddit",
  "com.reddit.Reddit": "reddit",
  "com.burbn.instagram": "instagram",
  "com.toyopagroup.picaboo": "snapchat",
  "com.snapchat.android": "snapchat",
  "com.ubercab.UberClient": "uber",
  "com.airbnb.app": "airbnb",
  "us.zoom.videomeetings": "zoom",
  "com.google.android.apps.maps": "googlemaps",
  "com.google.Gmail": "gmail",
  "com.google.android.gm": "gmail",
  "com.openai.chat": "openai",
  "com.openai.chatgpt": "openai",
  "com.booking.bookingapp": "bookingdotcom",
  "net.skyscanner.iphone": null,
  "com.apple.preferences": "settings",
  "com.withopal.opal": null,
  "wtf.riedel.one-sec": null,
  "to.freedom.freedomblocker": null,
  "com.screen0": "screen0",
  "com.amazon.aiv.aivapp": null,
  "com.cbs.canada.app": "paramountplus",
};

/** iOS Screen Time internal segment aliases (mirrors friendlyAppName.ts). */
const SEGMENT_ICON_SLUGS: Record<string, string> = {
  mobilemail: "applemail",
  mobilesafari: "safari",
  oneconnect4ios: "smartthings",
  youtube: "youtube",
  bookingapp: "bookingdotcom",
  preferences: "settings",
};

/** Human-readable labels → slug when unambiguous. */
const DISPLAY_NAME_ICON_SLUGS: Record<string, string> = {
  mail: "mailbox",
  safari: "safari",
  "google maps": "googlemaps",
  spotify: "spotify",
  whatsapp: "whatsapp",
  youtube: "youtube",
  instagram: "instagram",
  facebook: "facebook",
  messenger: "messenger",
  tiktok: "tiktok",
  discord: "discord",
  telegram: "telegram",
  netflix: "netflix",
  reddit: "reddit",
  snapchat: "snapchat",
  chrome: "googlechrome",
  firefox: "firefox",
  signal: "signal",
  uber: "uber",
  airbnb: "airbnb",
  zoom: "zoom",
  pinterest: "pinterest",
  smartthings: "smartthings",
  chatgpt: "openai",
  "booking.com": "bookingdotcom",
  booking: "bookingdotcom",
  preferences: "settings",
  settings: "settings",
  cbs: "paramountplus",
};

/** website category: domain (no scheme) → slug when we have a clear brand icon. */
const WEBSITE_DOMAIN_SLUGS: Record<string, string> = {
  "google.com": "google",
  "youtube.com": "youtube",
  "instagram.com": "instagram",
  "facebook.com": "facebook",
  "whatsapp.com": "whatsapp",
  "reddit.com": "reddit",
  "twitter.com": "x",
  "x.com": "x",
  "tiktok.com": "tiktok",
  "netflix.com": "netflix",
  "spotify.com": "spotify",
  "discord.com": "discord",
  "telegram.org": "telegram",
  "pinterest.com": "pinterest",
  "snapchat.com": "snapchat",
};

function normalizeSlug(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function normalizeDomain(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^web:/i, "")
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .split("/")[0]!
    .split("?")[0]!;
}

function isWebsiteCategory(category?: string | null): boolean {
  return category?.trim().toLowerCase() === "website";
}

function lastIdentifierSegment(identifier: string): string {
  return identifier.trim().split(".").pop()?.toLowerCase() ?? "";
}

function heuristicSlug(value: string): string | null {
  const slug = normalizeSlug(value);
  if (!slug || slug.length < 2) return null;
  return KNOWN_GOOD_SLUGS.has(slug) ? slug : null;
}

export function resolveAppIconSlug({ displayName, appIdentifier, category }: AppIconInput): string | null {
  const trimmedId = (appIdentifier ?? "").trim();
  const trimmedDisplay = (displayName ?? "").trim();
  const idKey = trimmedId.toLowerCase();

  if (isWebsiteCategory(category)) {
    const domainSource = trimmedDisplay || trimmedId.replace(/^web:/i, "");
    const domain = normalizeDomain(domainSource);
    const domainSlug = WEBSITE_DOMAIN_SLUGS[domain];
    if (domainSlug) return domainSlug;
    return null;
  }

  if (isIosScreenshotBundleId(trimmedId, trimmedDisplay)) {
    return IOS_SCREENSHOT_ICON_SLUG;
  }

  if (isIosInCallService(trimmedId, trimmedDisplay)) {
    return IOS_IN_CALL_ICON_SLUG;
  }

  if (idKey && idKey in IDENTIFIER_ICON_SLUGS) {
    return IDENTIFIER_ICON_SLUGS[idKey] ?? null;
  }

  const segment = lastIdentifierSegment(trimmedId || trimmedDisplay);
  if (segment && segment in SEGMENT_ICON_SLUGS) {
    return SEGMENT_ICON_SLUGS[segment]!;
  }

  const displayKey = trimmedDisplay.toLowerCase();
  if (displayKey && displayKey in DISPLAY_NAME_ICON_SLUGS) {
    return DISPLAY_NAME_ICON_SLUGS[displayKey]!;
  }

  const appleSystemApp = resolveAppleSystemApp({
    displayName: trimmedDisplay,
    appIdentifier: trimmedId,
  });
  if (appleSystemApp) {
    const localSvg = resolveLocalSvgIcon({
      displayName: appleSystemApp.officialDisplayName,
      appIdentifier: appleSystemApp.bundleId,
    });
    if (localSvg) return localSvg.slug;

    if (appleSystemApp.isFirstPartyApple) {
      const applePrefixedLocalSvg = resolveLocalSvgIcon({
        displayName: `Apple ${appleSystemApp.officialDisplayName}`,
        appIdentifier: appleSystemApp.bundleId,
      });
      if (applePrefixedLocalSvg) return applePrefixedLocalSvg.slug;
    }

    const officialSlug = heuristicSlug(appleSystemApp.officialDisplayName);
    if (officialSlug) return officialSlug;

    if (appleSystemApp.isFirstPartyApple) {
      const genericAppleSvg = resolveLocalSvgIcon({
        displayName: "Apple",
        appIdentifier: appleSystemApp.bundleId,
      });
      if (genericAppleSvg) return genericAppleSvg.slug;
    }
  }

  if (trimmedDisplay) {
    const fromDisplay = heuristicSlug(trimmedDisplay);
    if (fromDisplay) return fromDisplay;
  }

  if (segment) {
    const fromSegment = heuristicSlug(segment);
    if (fromSegment) return fromSegment;
  }

  const localSvg = resolveLocalSvgIcon({ displayName: trimmedDisplay, appIdentifier: trimmedId });
  if (localSvg) return localSvg.slug;

  return null;
}

/** Local SVGs that keep original colors (no brightness-0 invert in AppIcon). */
const FULL_COLOR_ICON_SLUGS = new Set(["screen0"]);

export function usesFullColorLocalIcon(slug: string): boolean {
  return FULL_COLOR_ICON_SLUGS.has(slug);
}

export function appIconUrl(slug: string): string {
  return `/svgicons/${slug}.svg`;
}
